import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { sharedStyles } from '../styles/shared-styles';
import type { GameTimeState } from '../../core/GameTime';

/**
 * Game Time Panel
 * 
 * Debug panel for controlling game time: pause, slow-motion, time scale.
 * Uses the gameTime singleton exposed via testHooks.
 */
@customElement('game-time-panel')
export class GameTimePanel extends LitElement {
  @state() private timeScale = 1.0;
  @state() private isPaused = false;
  @state() private isRamping = false;
  @state() private targetScale: number | null = null;
  
  private updateInterval?: ReturnType<typeof setInterval>;
  
  static styles = [
    sharedStyles,
    css`
    :host {
      display: block;
      color: white;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      padding: 16px;
    }
    
    .control-group {
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .control-group:last-of-type {
      border-bottom: none;
    }
    
    h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      opacity: 0.95;
      color: rgba(255, 255, 255, 0.9);
    }
    
    .status-row {
      display: flex;
      gap: 10px;
      align-items: center;
      margin: 8px 0;
      font-size: 12px;
    }
    
    .status-row .label {
      min-width: 80px;
      opacity: 0.75;
    }
    
    .status-row .value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      opacity: 0.95;
    }
    
    .pill {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 500;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }
    
    .pill.paused {
      border-color: rgba(255, 100, 100, 0.4);
      background: rgba(255, 100, 100, 0.1);
      color: #ff8080;
    }
    
    .pill.running {
      border-color: rgba(124, 252, 144, 0.4);
      background: rgba(124, 252, 144, 0.1);
      color: #7CFC90;
    }
    
    .pill.slow {
      border-color: rgba(255, 200, 100, 0.4);
      background: rgba(255, 200, 100, 0.1);
      color: #ffc864;
    }
    
    .pill.fast {
      border-color: rgba(100, 200, 255, 0.4);
      background: rgba(100, 200, 255, 0.1);
      color: #64c8ff;
    }
    
    .button-row {
      display: flex;
      gap: 8px;
      margin: 12px 0;
      flex-wrap: wrap;
    }
    
    button {
      padding: 6px 12px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      color: white;
      cursor: pointer;
      font-size: 12px;
      transition: background 0.15s, border-color 0.15s;
    }
    
    button:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }
    
    button:active {
      background: rgba(255, 255, 255, 0.2);
    }
    
    button.primary {
      background: rgba(100, 150, 255, 0.3);
      border-color: rgba(100, 150, 255, 0.5);
    }
    
    button.primary:hover {
      background: rgba(100, 150, 255, 0.4);
    }
    
    button.danger {
      background: rgba(255, 100, 100, 0.3);
      border-color: rgba(255, 100, 100, 0.5);
    }
    
    button.danger:hover {
      background: rgba(255, 100, 100, 0.4);
    }
    
    .slider-row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 8px 0;
    }
    
    .slider-row .label {
      min-width: 80px;
      opacity: 0.75;
      font-size: 12px;
    }
    
    .slider-row input[type="range"] {
      flex: 1;
      height: 4px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
      appearance: none;
      cursor: pointer;
    }
    
    .slider-row input[type="range"]::-webkit-slider-thumb {
      appearance: none;
      width: 14px;
      height: 14px;
      background: #7CFC90;
      border-radius: 50%;
      cursor: pointer;
    }
    
    .slider-row .value {
      min-width: 50px;
      text-align: right;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
    }
    
    .preset-buttons {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 6px;
      margin-top: 8px;
    }
    
    .preset-buttons button {
      padding: 8px 4px;
      font-size: 11px;
    }
    
    .hotkey {
      opacity: 0.5;
      font-size: 10px;
      margin-left: 4px;
    }
    
    .ramping-indicator {
      display: inline-block;
      margin-left: 8px;
      animation: pulse 0.5s infinite alternate;
    }
    
    @keyframes pulse {
      from { opacity: 0.5; }
      to { opacity: 1; }
    }
  `];
  
  connectedCallback() {
    super.connectedCallback();
    // Poll for state updates
    this.updateInterval = setInterval(() => this.syncState(), 100);
    this.syncState();
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }
  
  private getGameTime() {
    return (window as any).__himumTestHooks?.gameTime;
  }
  
  private syncState() {
    const gt = this.getGameTime();
    if (!gt) return;
    
    const state: GameTimeState = gt.getState();
    this.timeScale = state.timeScale;
    this.isPaused = state.isPaused;
    this.isRamping = state.isRamping;
    this.targetScale = state.targetScale;
  }
  
  private togglePause() {
    const gt = this.getGameTime();
    if (!gt) return;
    gt.togglePause();
    this.syncState();
  }
  
  private setTimeScale(scale: number) {
    const gt = this.getGameTime();
    if (!gt) return;
    gt.setTimeScale(scale);
    this.syncState();
  }
  
  private rampToScale(scale: number, duration: number = 0.3) {
    const gt = this.getGameTime();
    if (!gt) return;
    gt.rampTo(scale, duration, 'easeOut');
    this.syncState();
  }
  
  private slowMotion() {
    const gt = this.getGameTime();
    if (!gt) return;
    gt.slowMotion(0.2, 0.3);
    this.syncState();
  }
  
  private normalSpeed() {
    const gt = this.getGameTime();
    if (!gt) return;
    gt.normalSpeed(0.3);
    this.syncState();
  }
  
  private fastForward() {
    const gt = this.getGameTime();
    if (!gt) return;
    gt.fastForward(2.0, 0.2);
    this.syncState();
  }
  
  private onSliderChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.setTimeScale(value);
  }
  
  private getStatusPill() {
    if (this.isPaused) {
      return html`<span class="pill paused">PAUSED</span>`;
    }
    if (this.timeScale < 0.5) {
      return html`<span class="pill slow">SLOW-MO</span>`;
    }
    if (this.timeScale > 1.5) {
      return html`<span class="pill fast">FAST</span>`;
    }
    return html`<span class="pill running">RUNNING</span>`;
  }
  
  render() {
    const gt = this.getGameTime();
    if (!gt) {
      return html`<div style="opacity: 0.5">GameTime not available</div>`;
    }
    
    return html`
      <div class="control-group">
        <h3>Time Status</h3>
        <div class="status-row">
          <span class="label">State:</span>
          ${this.getStatusPill()}
          ${this.isRamping ? html`<span class="ramping-indicator">→ ${this.targetScale?.toFixed(2)}x</span>` : ''}
        </div>
        <div class="status-row">
          <span class="label">Time Scale:</span>
          <span class="value">${this.timeScale.toFixed(2)}x</span>
        </div>
      </div>
      
      <div class="control-group">
        <h3>Quick Controls</h3>
        <div class="button-row">
          <button 
            class="${this.isPaused ? 'primary' : ''}" 
            @click=${this.togglePause}
          >
            ${this.isPaused ? '▶ Resume' : '⏸ Pause'}
            <span class="hotkey">(P)</span>
          </button>
          <button @click=${this.slowMotion}>
            🐢 Slow-Mo
            <span class="hotkey">(L)</span>
          </button>
          <button @click=${this.normalSpeed}>
            ▶ Normal
          </button>
          <button @click=${this.fastForward}>
            ⏩ 2x Speed
          </button>
        </div>
      </div>
      
      <div class="control-group">
        <h3>Time Scale</h3>
        <div class="slider-row">
          <span class="label">Scale:</span>
          <input
            type="range"
            min="0"
            max="3"
            step="0.1"
            .value=${String(this.timeScale)}
            @input=${this.onSliderChange}
          />
          <span class="value">${this.timeScale.toFixed(1)}x</span>
        </div>
        <div class="preset-buttons">
          <button @click=${() => this.setTimeScale(0.1)}>0.1x</button>
          <button @click=${() => this.setTimeScale(0.25)}>0.25x</button>
          <button @click=${() => this.setTimeScale(0.5)}>0.5x</button>
          <button @click=${() => this.setTimeScale(1.0)}>1x</button>
          <button @click=${() => this.setTimeScale(1.5)}>1.5x</button>
          <button @click=${() => this.setTimeScale(2.0)}>2x</button>
          <button @click=${() => this.setTimeScale(3.0)}>3x</button>
          <button @click=${() => this.rampToScale(1.0, 1.0)}>Ramp→1x</button>
        </div>
      </div>
      
      <div class="control-group">
        <h3>Keyboard Shortcuts</h3>
        <div class="status-row">
          <span class="label">P:</span>
          <span class="value">Toggle Pause</span>
        </div>
        <div class="status-row">
          <span class="label">L:</span>
          <span class="value">Toggle Slow-Mo</span>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'game-time-panel': GameTimePanel;
  }
}
